import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Shield,
  LogOut,
  ExternalLink,
  Sparkles,
  Menu,
  Search,
  X,
  ChevronRight,
} from 'lucide-react';
import {
  menuCategories,
  userCategoryKeys,
  adminCategoryKeys,
  adminSectionGroups,
  MenuCategory,
} from '@/config/menuConfig';

// ── Section meta ──────────────────────────────────────────────────────────────
const sectionMeta: Record<string, { label: string }> = {
  CRM:            { label: 'CRM' },
  Work:           { label: 'Work' },
  Finance:        { label: 'Finance' },
  HR:             { label: 'HR' },
  Growth:         { label: 'Growth' },
  Business:       { label: 'Business' },
  Content:        { label: 'Content' },
  Communication:  { label: 'Communication' },
  'AI & SaaS':   { label: 'AI & SaaS' },
  System:         { label: 'System' },
};

// ── Quick Search ──────────────────────────────────────────────────────────────
const QuickSearch = ({ query, onChange }: { query: string; onChange: (v: string) => void }) => (
  <div className="relative px-1 mb-1">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-sidebar-foreground/40 pointer-events-none" />
    <Input
      value={query}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Quick search..."
      className="h-9 pl-9 pr-7 text-[13px] bg-sidebar-accent border-sidebar-border text-sidebar-foreground placeholder:text-sidebar-foreground/40 focus-visible:ring-1 focus-visible:ring-sidebar-ring focus-visible:border-sidebar-ring rounded-md"
    />
    {query && (
      <button
        onClick={() => onChange('')}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-sidebar-foreground/40 hover:text-sidebar-foreground transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

// ── Collapsible Section ───────────────────────────────────────────────────────
const CollapsibleSection = ({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center gap-2 px-3 pt-4 pb-1.5 group">
          <ChevronRight
            className={cn(
              'w-3 h-3 shrink-0 text-sidebar-foreground/30 group-hover:text-sidebar-foreground/55 transition-transform duration-200 ease-in-out',
              open && 'rotate-90'
            )}
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-sidebar-foreground/40 group-hover:text-sidebar-foreground/65 transition-colors select-none">
            {label}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

// ── Nav Item (flat, no collapsible) ───────────────────────────────────────────
const NavItem = ({
  path,
  icon: Icon,
  label,
  currentPath,
  onNavigate,
}: {
  path: string;
  icon: any;
  label: string;
  currentPath: string;
  onNavigate: (path: string) => void;
}) => {
  const isActive = currentPath === path;
  return (
    <button
      onClick={() => onNavigate(path)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-100',
        isActive
          ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent'
      )}
    >
      <Icon className={cn('w-4 h-4 shrink-0', isActive ? 'opacity-100' : 'text-sidebar-foreground/45')} />
      <span className="truncate">{label}</span>
    </button>
  );
};

// ── Search Results ────────────────────────────────────────────────────────────
const SearchResults = ({
  query,
  allItems,
  onNavigate,
  currentPath,
}: {
  query: string;
  allItems: { path: string; label: string; icon: any; categoryLabel: string }[];
  onNavigate: (path: string) => void;
  currentPath: string;
}) => {
  const results = allItems.filter(
    (i) =>
      i.label.toLowerCase().includes(query.toLowerCase()) ||
      i.categoryLabel.toLowerCase().includes(query.toLowerCase())
  );

  if (results.length === 0)
    return (
      <div className="py-8 text-center text-xs text-sidebar-foreground/35">
        No results for "{query}"
      </div>
    );

  return (
    <div className="px-2 space-y-0.5">
      {results.map((item) => {
        const Icon = item.icon;
        const isActive = currentPath === item.path;
        return (
          <button
            key={item.path}
            onClick={() => onNavigate(item.path)}
            className={cn(
              'w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-[13px] transition-colors',
              isActive
                ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                : 'text-sidebar-foreground/65 hover:text-sidebar-foreground hover:bg-sidebar-accent/70'
            )}
          >
            <Icon className="w-3.5 h-3.5 shrink-0" />
            <div className="flex flex-col items-start min-w-0">
              <span className="truncate leading-tight">{item.label}</span>
              <span className="text-[10px] opacity-50 leading-tight">{item.categoryLabel}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ── Sidebar Nav Content ───────────────────────────────────────────────────────
interface SidebarNavContentProps {
  hasCategoryAccess: (k: string) => boolean;
  hasMenuAccess: (path: string, basicUser?: boolean) => boolean;
  onNavigate: (path: string) => void;
  currentPath: string;
  isAdminUser: boolean;
  signOut: () => void;
  user: { email?: string; user_metadata?: { avatar_url?: string; full_name?: string } } | null;
}

const SidebarNavContent = ({
  hasCategoryAccess,
  hasMenuAccess,
  onNavigate,
  currentPath,
  isAdminUser,
  signOut,
  user,
}: SidebarNavContentProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const allSearchableItems = useMemo(() => {
    const items: { path: string; label: string; icon: any; categoryLabel: string }[] = [];
    [...userCategoryKeys, ...adminCategoryKeys].forEach((key) => {
      const cat = menuCategories[key];
      if (!cat) return;
      cat.items.forEach((item) => {
        items.push({ path: item.path, label: item.label, icon: item.icon, categoryLabel: cat.label });
      });
    });
    return items;
  }, []);

  // Group admin keys by sectionGroup
  const groupedAdmin = useMemo(() => {
    const groups: Record<string, string[]> = {};
    adminCategoryKeys.forEach((key) => {
      const cat = menuCategories[key];
      if (!cat) return;
      const group = cat.sectionGroup || 'Other';
      if (!groups[group]) groups[group] = [];
      groups[group].push(key);
    });
    return groups;
  }, []);

  const hasAnyAdminAccess = adminCategoryKeys.some((k) => hasCategoryAccess(k));

  // Render all items of a category key as flat items
  const renderCategoryItems = (key: string) => {
    const cat = menuCategories[key];
    if (!cat || !hasCategoryAccess(key)) return null;
    const items = cat.items.filter((i) => hasMenuAccess(i.path, i.basicUser));
    if (items.length === 0) return null;
    return items.map((item) => (
      <NavItem
        key={item.path}
        path={item.path}
        icon={item.icon}
        label={item.label}
        currentPath={currentPath}
        onNavigate={onNavigate}
      />
    ));
  };

  // Render user sections with "MAIN" label
  const renderUserSection = () => {
    const allItems = userCategoryKeys.flatMap((key) => {
      const cat = menuCategories[key];
      if (!cat || !hasCategoryAccess(key)) return [];
      return cat.items
        .filter((i) => hasMenuAccess(i.path, i.basicUser))
        .map((item) => ({ ...item, categoryKey: key }));
    });
    if (allItems.length === 0) return null;
    return (
      <div>
        <div className="px-3 pt-3 pb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-sidebar-foreground/40 select-none">Main</span>
        </div>
        {allItems.map((item) => (
          <NavItem
            key={item.path}
            path={item.path}
            icon={item.icon}
            label={item.label}
            currentPath={currentPath}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground">
      {/* User strip */}
      <div className="px-3 py-4 border-b border-sidebar-border flex items-center gap-3">
        <Avatar className="w-9 h-9 shrink-0 ring-2 ring-sidebar-border">
          <AvatarImage src={user?.user_metadata?.avatar_url} />
          <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-sm font-semibold">
            {user?.email?.[0]?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-sidebar-foreground truncate leading-tight">
            {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
          </p>
          <p className="text-[11px] text-sidebar-foreground/50 truncate leading-tight mt-0.5">
            {isAdminUser ? 'Administrator' : 'Member'}
          </p>
        </div>
        {isAdminUser && (
          <Badge className="text-[10px] h-5 px-2 bg-sidebar-primary text-sidebar-primary-foreground border-0 font-semibold shrink-0">
            Admin
          </Badge>
        )}
      </div>

      {/* Scrollable nav */}
      <ScrollArea className="flex-1">
        <nav className="px-2 py-3">
          {/* Search */}
          <div className="pb-2 pt-0">
            <QuickSearch query={searchQuery} onChange={setSearchQuery} />
          </div>

          {searchQuery ? (
            <SearchResults
              query={searchQuery}
              allItems={allSearchableItems}
              onNavigate={(path) => { onNavigate(path); setSearchQuery(''); }}
              currentPath={currentPath}
            />
          ) : (
            <>
              {/* ── User section (flat) ── */}
              {renderUserSection()}

              {/* ── Admin sections ── */}
              {hasAnyAdminAccess && (
                <>
                  <div className="px-1 pt-5 pb-1">
                    <Separator className="bg-sidebar-border/50" />
                  </div>
                  <div className="flex items-center gap-1.5 px-3 pb-1">
                    <Shield className="w-2.5 h-2.5 text-sidebar-foreground/35" />
                    <span className="text-[9px] font-bold text-sidebar-foreground/35 uppercase tracking-[0.12em]">
                      {isAdminUser ? 'Admin Panel' : 'Tools'}
                    </span>
                  </div>

                  {adminSectionGroups.map((groupName) => {
                    const keys = groupedAdmin[groupName];
                    if (!keys?.length) return null;

                    const anyVisible = keys.some(
                      (k) =>
                        hasCategoryAccess(k) &&
                        menuCategories[k]?.items.some((i) => hasMenuAccess(i.path, i.basicUser))
                    );
                    if (!anyVisible) return null;

                    const meta = sectionMeta[groupName] || { label: groupName };
                    return (
                      <CollapsibleSection key={groupName} label={meta.label} defaultOpen={false}>
                        {keys.map((key) => renderCategoryItems(key))}
                      </CollapsibleSection>
                    );
                  })}
                </>
              )}
            </>
          )}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="p-2 border-t border-sidebar-border/60 space-y-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open('/', '_blank')}
          className="w-full justify-center gap-2 text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent text-xs h-8"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View Website
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="w-full justify-center gap-2 text-destructive/60 hover:text-destructive hover:bg-destructive/10 text-xs h-8"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
export const ResponsiveSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { loading: permissionsLoading, canAccessMenu, canAccessCategory, isAdmin } = usePermissions();

  const isAdminUser = !permissionsLoading && isAdmin();

  const hasMenuAccess = (path: string, basicUser?: boolean) => {
    if (permissionsLoading) return false;
    if (basicUser) return true;
    return canAccessMenu(path);
  };

  const hasCategoryAccess = (key: string) => {
    if (permissionsLoading) return false;
    return canAccessCategory(key);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navContentProps: SidebarNavContentProps = {
    hasCategoryAccess,
    hasMenuAccess,
    onNavigate: handleNavigation,
    currentPath: location.pathname,
    isAdminUser,
    signOut,
    user,
  };

  return (
    <TooltipProvider delayDuration={300}>
      <>
        {/* ── Mobile header ─────────────────────────────────────────────── */}
        <header className="lg:hidden fixed top-0 left-0 right-0 h-14 z-[60] bg-background/95 backdrop-blur-xl border-b border-border">
          <div className="h-full px-4 flex items-center justify-between">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="w-9 h-9">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[260px] p-0 bg-sidebar border-r border-sidebar-border flex flex-col"
              >
                {/* Mobile logo */}
                <div className="px-4 py-3.5 border-b border-sidebar-border/60 flex items-center gap-2.5">
                  <img src="/logo/pitech-logo.png" alt="PiTech" className="h-7 w-auto" />
                  <div>
                    <p className="text-sm font-bold text-sidebar-foreground leading-tight">Dashboard</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Sparkles className="w-2.5 h-2.5 text-sidebar-foreground/40" />
                      <span className="text-[9px] font-semibold text-sidebar-foreground/40 uppercase tracking-wider">Premium</span>
                    </div>
                  </div>
                </div>
                <div className="flex-1 overflow-hidden">
                  <SidebarNavContent {...navContentProps} />
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleNavigation('/dashboard')}>
              <img src="/logo/pitech-logo.png" alt="PiTech" className="h-6 w-auto" />
              <span className="text-sm font-bold text-foreground">Dashboard</span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="w-9 h-9 rounded-full p-0"
              onClick={() => handleNavigation('/dashboard/settings')}
            >
              <Avatar className="w-8 h-8 ring-2 ring-border">
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </div>
        </header>

        {/* ── Desktop header ─────────────────────────────────────────────── */}
        <header className="hidden lg:flex fixed top-0 left-0 right-0 h-14 z-[60] bg-background/95 backdrop-blur-xl border-b border-border">
          <div className="h-full px-5 flex items-center justify-between w-full">
            <button
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            >
              <img src="/logo/pitech-logo.png" alt="PiTech" className="h-8 w-auto" />
              <div className="text-left">
                <p className="text-sm font-bold text-foreground leading-tight">Dashboard</p>
                <div className="flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5 text-muted-foreground" />
                  <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Premium</span>
                </div>
              </div>
            </button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-10 h-10 rounded-full p-0"
                  onClick={() => navigate('/dashboard/settings')}
                >
                  <Avatar className="w-9 h-9 ring-2 ring-border hover:ring-primary/40 transition-all">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                      {user?.email?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Profile Settings</TooltipContent>
            </Tooltip>
          </div>
        </header>

        {/* ── Desktop sidebar ────────────────────────────────────────────── */}
        <aside className="hidden lg:flex lg:flex-col h-[calc(100vh-3.5rem)] w-56 fixed left-0 top-14 z-50 bg-sidebar border-r border-sidebar-border/60">
          <SidebarNavContent {...navContentProps} />
        </aside>
      </>
    </TooltipProvider>
  );
};
